import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, ChevronRight, Zap, Shield, Award, Tag, Menu, X, ArrowRight, Wrench, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppV3() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedMotor, setSelectedMotor] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', organization: '',
    inquiry_type: 'General Inquiry', message: '', hear_about_us: 'Google'
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.status === 200) {
        setFormStatus({ type: 'success', message: 'Message sent! We\'ll get back to you soon.' });
        setFormData({
          name: '', email: '', phone: '', organization: '',
          inquiry_type: 'General Inquiry', message: '', hear_about_us: 'Google'
        });
      }
    } catch (error) {
      setFormStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Full product catalog
  const products = [
    // Electric Pump Systems
    { id: 'belt-driven-pump', name: '4x4 Self-Priming Belt Driven Reserve Pit Pump', category: 'Electric Pump Systems', description: 'Designed for reliable performance in reserve pit applications. This belt-driven pump offers self-priming capabilities.', img: '/photos/pump_belt_driven.png', details: ['25 HP', '1,800 RPM', 'TEFC Motor', 'Class 1 Div 2 pump panel', 'Size 2 motor starter', 'Class 10 adjustable overload block', 'Gorman Rupp self-priming pump', 'Custom plumbing available', 'Other custom configurations available'] },
    { id: 'direct-drive-pump', name: '4x4 Self-Priming Direct Drive Reserve Pit Pump', category: 'Electric Pump Systems', description: 'A direct-drive self-priming pump system ideal for reserve pit operations, offering efficient power transfer.', img: '/photos/pump_direct_drive.png', details: ['25 HP', '1,800 RPM', 'TEFC Motor', 'Class 1 Div 2 pump panel', 'Size 2 motor starter', 'Class 10 adjustable overload block', 'Gorman Rupp self-priming pump', 'Custom plumbing available', 'Other custom configurations available'] },
    { id: 'centrifugal-pump', name: '5x6 Centrifugal Pump — Reserve Pit Pump', category: 'Electric Pump Systems', description: 'A versatile centrifugal pump configurable for various applications, including reserve pit pumping. Can be built as a self-priming unit.', img: '/photos/pump_centrifugal.png', details: ['Reserve pit pump', 'Can be built as a 4x4 self-priming pump', 'Can be built as a 5x6 centrifugal pump', 'Customized plumbing available'] },
    { id: 'centrifugal-75hp', name: '5x6 Centrifugal Pump with 75 HP Motor', category: 'Electric Pump Systems', description: 'A powerful centrifugal pump system featuring a 75 HP motor, suitable for demanding industrial applications.', img: '/photos/pump_centrifugal_75hp.png', details: ['75 HP', '1,800 RPM', 'TEFC Motor', 'Class 1 Div 1 explosion-proof panel', 'Size 4 motor starter', 'Class 10 adjustable overload block', 'Custom plumbing available'] },

    // Power Distribution & Control
    { id: 'camp-transformers', name: 'Camp Transformers', category: 'Power Distribution & Control', description: 'Diverse selection of camp transformers designed to meet your electrical needs in remote locations.', img: '/photos/transformer_skid.png', details: ['Wide range of transformer sizes', 'Custom receptacles available', 'Fabricated skid', 'Slanted roof'] },
    { id: 'scr-house', name: 'SCR House', category: 'Power Distribution & Control', description: 'Centralized power distribution for drilling and industrial operations. Power is brought in from the highline to a transformer, which then feeds the SCR House. Inside, the SCR House contains the main breakers, distribution panels, control sections, and safety disconnects needed to safely manage and send power to equipment across the job site.', img: '/photos/scr_house_1.jpeg', details: ['Main incoming power section', 'Main breakers', 'Multiple circuit breakers for equipment feeds', 'Electrical distribution panels', 'Motor/control power sections', 'Bus bars and wiring connections', 'Breaker controls and switches', 'Safety disconnects', 'Grounding connections', 'Ventilation/cooling system', 'Interior lighting', 'Heavy-duty enclosure for field use'] },
    { id: 'pump-panels', name: 'Pump Panels', category: 'Power Distribution & Control', description: 'Custom-built pump control panels engineered for industrial and oilfield pump applications.', img: '/photos/pump_panel_new.png', details: ['Class 1 Div 2 rated', 'Motor starters', 'Overload protection', 'Custom configurations'] },
    { id: 'explosion-proof', name: 'Explosion-Proof Panels', category: 'Power Distribution & Control', description: 'Class 1 Div 1 explosion-proof control panels for hazardous location operations.', img: '/photos/explosion_proof_new.png', details: ['Class 1 Div 1 rated', 'Hazardous location certified', 'Heavy-duty construction', 'Custom configurations'] },
    { id: 'control-boards', name: 'Motor Control Center', category: 'Power Distribution & Control', description: 'Motor Control Centers and custom control boards for industrial automation and process control.', img: '/photos/plc_panel.jpeg', details: ['PLC integration', 'HMI touchscreens available', 'Custom programming', 'NEMA / IEC starters'] },
    { id: 'panels', name: 'Drive Panels & VFDs', category: 'Power Distribution & Control', description: 'Variable Frequency Drive panels and drive panel solutions for precise motor control.', img: '/photos/drive_panel.png', details: ['VFD integration', 'Custom panel builds', 'Wiring panels', 'Breaker panels'] },

    // Motors
    { id: 'ac-motors', name: 'AC Electric Motors', category: 'Motors', description: 'Full range of AC electric motors — from small water tank motors to 1,000+ HP drilling Drawworks motors.', img: '/photos/ac_motor_new.png', details: ['TEFC motor options', 'Explosion-proof options', 'Multiple HP ranges', 'Custom configurations'] },

    // Specialty Equipment
    { id: 'pressure-washers', name: 'Pressure Washers', category: 'Specialty Equipment', description: 'High-performance electric pressure washer systems, available in skid-mounted or trailer-mounted configurations, for effective cleaning and washdown applications.', img: '/photos/pressure_washer.webp', details: ['Electric pressure washer system', 'Skid-mounted or trailer-mounted', 'Motor-driven pump', 'Control panel', 'Hose reel', 'High-pressure hose', 'Spray gun / wand', 'Custom electrical setup', 'Custom plumbing'] },
    {
      id: 'belt-driven-pressure-washer',
      name: 'Belt-Driven Industrial Pressure Washer',
      category: 'Specialty Equipment',
      description: 'Heavy-duty, belt-driven pressure washer designed for dependable performance in industrial and oilfield applications. Built on a durable steel skid and powered by an electric motor with a commercial-grade pump.',
      img: '/photos/belt_pressure_washer_1.jpg',
      images: ['/photos/belt_pressure_washer_1.jpg', '/photos/belt_pressure_washer_2.jpg', '/photos/belt_pressure_washer_3.jpg'],
      details: [
        'Belt-driven design',
        'Electric motor powered',
        'Commercial-grade pump',
        'Durable steel skid',
        'Integrated motor starter enclosure',
        'Built for industrial & oilfield use',
        'Hose, spray gun, wand & accessories sold separately'
      ],
      priceTables: [
        {
          title: 'Pricing',
          rows: [
            ['Belt-Driven Industrial Pressure Washer', '$5,800']
          ]
        }
      ]
    },
    { id: 'generator-houses', name: 'Generator Houses', category: 'Specialty Equipment', description: 'Pre-engineered generator houses for remote power generation. Built tough for oilfield environments.', img: '/photos/generator_house_1.jpeg', details: ['Custom sizing available', 'Weather-resistant enclosure', 'Built for field use', 'Multiple configurations'] },
    { id: 'lighting-systems', name: 'Lighting Systems', category: 'Specialty Equipment', description: 'Industrial-grade yard and facility lighting systems designed for harsh environments and demanding operations.', img: '/photos/lighting_systems.png', details: ['Yard lighting', 'Industrial-grade fixtures', 'Custom installation', 'LED options available'] },
    { id: 'generator', name: 'Generator', category: 'Specialty Equipment', description: 'Industrial-grade diesel and natural gas generators for continuous and standby power applications.', img: '/photos/generator.jpeg', details: ['Multiple kW options', 'Diesel or natural gas', 'Auto-start capabilities', 'Service & support included'] },
    { id: 'skid-mounted', name: 'Skid Mounted Packages', category: 'Specialty Equipment', description: 'Complete skid-mounted equipment packages for portable, ready-to-deploy industrial operations.', img: '/photos/skid_2.png', details: ['Pre-wired and tested', 'Custom configurations', 'Portable design', 'Quick deployment'] },
    { id: 'skid', name: 'Custom Skids', category: 'Specialty Equipment', description: 'Fabricated custom skids for mounting electrical equipment, pumps, and control systems.', img: '/photos/skid_1.jpeg', details: ['Custom fabrication', 'Heavy-duty construction', 'Multiple sizes', 'Built for field use'] },
    { id: 'air-compressor', name: 'Air Compressors', category: 'Specialty Equipment', description: 'Industrial air compressor systems for oilfield and industrial applications.', img: '/photos/air_compressor.jpeg', details: ['Industrial-grade', 'Custom configurations', 'Field-ready', 'Service & support'] },
    { id: 'explosion-proof-starters', name: 'Explosion-Proof Starters & Disconnects', category: 'Specialty Equipment', description: 'Heavy-duty explosion-proof motor starters and disconnect switches engineered for hazardous locations and demanding oilfield environments.', img: '/photos/explosion_proof_starters.png', details: ['Class 1 Div 1 rated', 'Hazardous location certified', 'Motor starters & disconnects', 'Heavy-duty construction', 'Multiple sizes available', 'Built for harsh environments'] },

    // Consumables & Transfer
    { id: 'chemical-injection', name: 'Chemical Injection Trailer', category: 'Consumables & Transfer', description: 'Trailer-mounted system providing precise chemical injection and flow control for fracking and water treatment operations. Features HMI touchscreen for easy operation and comprehensive monitoring. Doubles as a flowmeter trailer with full control system integration.', img: '/photos/chemical_injection_cover.jpeg', images: ['/photos/chemical_injection_cover.jpeg', '/photos/chemical_injection_2.jpg', '/photos/chemical_injection_3.jpg'], details: ['Trailer-mounted flowmeter system', 'HMI touchscreen control panel', 'PLC controls', 'Water flow monitoring', 'Chemical dosage control', '4–20 mA analog signal', 'Network communication option', 'Chemical selection screen', 'Dosage entry (gallons per 1,000 barrels)', 'Flow rate display', 'Totalizer / volume tracking', 'Start / stop controls', 'Alarm & fault display', 'Can be used as a show/demo trailer'] },

    // Enclosures
    {
      id: 'nema-enclosures',
      name: 'NEMA-Rated Electrical Enclosures',
      category: 'Enclosures',
      description: 'Custom-fabricated NEMA-rated electrical enclosures and back plates built in-house for industrial, oilfield, and control system applications. Available in a wide range of sizes — from compact flat panels to large double-door cabinets and vented VFD enclosures. Built to order with optional handles, single or double doors, and vented hoods to meet your project specs.',
      img: '/photos/nema_enclosures.png',
      images: ['/photos/nema_enclosures.png', '/photos/nema_enclosures_2.png', '/photos/nema_enclosures_3.png', '/photos/nema_enclosures_4.png', '/photos/nema_enclosures_5.png'],
      details: [
        'NEMA-rated construction',
        'Custom in-house fabrication',
        'Single & double door options',
        'Optional handles & vented hoods',
        'Back plates available',
        'VFD-specific enclosures',
        'Multiple sizes — flat to 65"',
        'Built to order'
      ],
      priceTables: [
        {
          title: 'Enclosures',
          rows: [
            ['9X9X.104 Enclosure Flat', '$19.21'],
            ['13X13X.104 Enclosure Flat', '$34.53'],
            ['24X24X12 Enclosure', '$425.70'],
            ['24X24X12 Enclosure With Handle', '$569.14'],
            ['36X36X10 Enclosure – Single Door', '$631.09'],
            ['36X36X12 Enclosure – Single Door', '$648.05'],
            ['36X36X12 Enclosure With Handle', '$791.52'],
            ['48X36X12 Enclosure – Single Door', '$775.13'],
            ['48X48X12 Enclosure – Double Door', '$1,851.75']
          ]
        },
        {
          title: 'Back Plates',
          rows: [
            ['21X21X.88 Enclosure Back Plate Bent', '$74.72']
          ]
        },
        {
          title: 'VFD Enclosures',
          rows: [
            ['VFD Enclosure – 29" T x 24" W x 12" D – Vented Hood', '$994.02'],
            ['65X36X16 VFD Enclosure', '$2,763.97']
          ]
        }
      ]
    },
  ];

  const categories = ['All', 'Electric Pump Systems', 'Power Distribution & Control', 'Motors', 'Specialty Equipment', 'Consumables & Transfer', 'Enclosures'];
  
  const filteredProducts = activeCategory === 'All' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  // Used motor stock with detailed pricing
  const usedMotors = [
    { brand: 'Teco', stock: 6, model: '15HP 1200RPM 284T', price: '$900', notes: 'Limited availability' },
    { brand: 'Teco', stock: 1, model: '10HP 3600RPM 215T', price: '$1,127.40', notes: 'Limited availability' },
    { brand: 'Teco', stock: 1, model: '75HP 1200RPM 405T', price: '$3,000', notes: 'Limited availability' },
    { brand: 'Teco', stock: 1, model: '75HP 1800RPM 365T', price: '$5,315.52', notes: 'Limited availability' },
    { brand: 'Teco', stock: 2, model: '7.5HP 1200RPM 254T EXP', price: '$1,750.37', notes: 'Limited availability' },
    { brand: 'Weg', stock: 3, model: '20HP 1200RPM 286T EXP', price: '$900', notes: 'Limited availability' },
    { brand: 'Weg', stock: 1, model: '1.5HP 1200RPM 182T EXP', price: '$403.87', notes: 'Limited availability' },
    { brand: 'Lincoln', stock: 1, model: '125HP 1800RPM 405T', price: '$6,500', notes: 'Limited availability' },
    { brand: 'Baldor', stock: 1, model: '40HP 1800RPM 324TZ', price: '$2,536.33', notes: 'Limited availability' },
    { brand: 'GE', stock: 1, model: '20HP 1800RPM 256T EXP', price: '$1,795.20', notes: 'Limited availability' },
    { brand: 'Toshiba', stock: 1, model: '15HP 1200RPM 284T', price: '$600', notes: 'Limited availability' },
    { brand: 'Emerson', stock: 1, model: '75HP 1800RPM 365T', price: '$5,315.15', notes: 'Limited availability' },
  ];

  // Camlock connectors - all $175 each
  const camlockImages = ['/photos/camlock_1.jpeg', '/photos/camlock_2.jpeg', '/photos/camlock_3.jpeg', '/photos/camlock_4.jpeg'];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="impulse-electric-v3">
      {/* Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md shadow-2xl border-b border-zinc-900' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/assets/impulse_logo.png" alt="Impulse Electric" className="w-12 h-12 object-contain rounded-lg" />
              <div>
                <h1 className="text-xl font-black tracking-tight">IMPULSE ELECTRIC</h1>
                <p className="text-[10px] text-red-500 font-bold tracking-[0.2em]">EST. 1987 · ODESSA, TX</p>
              </div>
            </div>
            
            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium hover:text-red-500 transition tracking-wide">HOME</button>
              <button onClick={() => scrollToSection('services')} className="text-sm font-medium hover:text-red-500 transition tracking-wide">SERVICES</button>
              <button onClick={() => scrollToSection('products')} className="text-sm font-medium hover:text-red-500 transition tracking-wide">PRODUCTS</button>
              <button onClick={() => scrollToSection('sale')} className="text-sm font-medium hover:text-red-500 transition tracking-wide">SALE</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium hover:text-red-500 transition tracking-wide">ABOUT</button>
              <button onClick={() => scrollToSection('contact')} className="bg-red-600 hover:bg-red-700 px-6 py-2.5 rounded-md font-semibold text-sm tracking-wide transition shadow-lg shadow-red-600/30">
                GET QUOTE
              </button>
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-white" data-testid="mobile-menu-toggle-v3">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-zinc-900 pt-4" data-testid="mobile-menu-v3">
              <div className="flex flex-col gap-3">
                <button onClick={() => scrollToSection('home')} className="text-left text-white hover:text-red-500 font-medium py-2">HOME</button>
                <button onClick={() => scrollToSection('services')} className="text-left text-white hover:text-red-500 font-medium py-2">SERVICES</button>
                <button onClick={() => scrollToSection('products')} className="text-left text-white hover:text-red-500 font-medium py-2">PRODUCTS</button>
                <button onClick={() => scrollToSection('sale')} className="text-left text-white hover:text-red-500 font-medium py-2">SALE</button>
                <button onClick={() => scrollToSection('about')} className="text-left text-white hover:text-red-500 font-medium py-2">ABOUT</button>
                <button onClick={() => scrollToSection('contact')} className="bg-red-600 px-6 py-2 rounded-md font-semibold text-center">GET QUOTE</button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section-v3">
        <div className="absolute inset-0">
          <img src="/photos/scr_house_2.jpeg" alt="SCR House" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-red-950/40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10 pt-20">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-600/30 rounded-full px-4 py-2 mb-8">
              <Zap size={16} className="text-red-500" />
              <span className="text-sm font-medium text-red-400">PROUDLY SERVING WEST TEXAS SINCE 1987</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black leading-none mb-6 tracking-tight">
              FROM WEST TEXAS<br/>
              TO THE FIELD —<br/>
              <span className="text-red-600">POWER THAT PERFORMS</span>
            </h1>
            
            <p className="text-xl text-zinc-400 mb-10 max-w-2xl leading-relaxed">
              Expert electrical power and drive solutions for manufacturers operating in remote and rugged environments — from small water tank motors to 1,000+ HP Drawworks.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection('contact')} className="bg-red-600 hover:bg-red-700 px-8 py-4 rounded-md font-bold tracking-wide transition shadow-xl shadow-red-600/30 flex items-center justify-center gap-2 group" data-testid="hero-quote-btn-v3">
                REQUEST A QUOTE
                <ArrowRight size={20} className="group-hover:translate-x-1 transition" />
              </button>
              <button onClick={() => scrollToSection('products')} className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 px-8 py-4 rounded-md font-bold tracking-wide transition flex items-center justify-center gap-2" data-testid="hero-products-btn-v3">
                VIEW PRODUCTS
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-20 max-w-md">
              <div className="border-l-2 border-red-600 pl-4">
                <div className="text-4xl font-black text-white">35+</div>
                <div className="text-sm text-zinc-400 tracking-wide">YEARS</div>
              </div>
              <div className="border-l-2 border-red-600 pl-4">
                <div className="text-4xl font-black text-white">24/7</div>
                <div className="text-sm text-zinc-400 tracking-wide">SUPPORT</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-zinc-950" data-testid="services-section-v3">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="text-red-500 font-bold tracking-widest text-sm mb-3">WHAT WE DO</div>
              <h2 className="text-5xl md:text-6xl font-black tracking-tight">
                Our <span className="text-red-600">Services</span>
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-lg">
              From commercial installations to emergency repairs, we power West Texas with expert electrical solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Field Service", desc: "24/7 rapid response when you need us most", img: "/photos/field_service_truck.png", images: ["/photos/field_service_truck.png"] },
              { title: "Commercial", desc: "Full-service electrical for businesses and offices", img: "/photos/service.png", images: ["/photos/service.png", "/photos/custom_panel_build_2.jpeg"] },
              { title: "Drilling Rig", desc: "Heavy-duty solutions for drilling rigs and oilfield operations", img: "/photos/scr_house_1.jpeg", images: ["/photos/scr_house_1.jpeg", "/photos/scr_house_2.jpeg"] }
            ].map((service, idx) => (
              <div key={service.title} onClick={() => { setSelectedService(service); setCurrentImageIdx(0); }} className="group relative overflow-hidden rounded-xl bg-zinc-900 border border-zinc-800 hover:border-red-600 transition-all duration-300 cursor-pointer" data-testid={`service-v3-${idx}`}>
                <div className="h-64 overflow-hidden">
                  <img src={service.img} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-black mb-2 tracking-tight">{service.title}</h3>
                  <p className="text-sm text-zinc-300 mb-4">{service.desc}</p>
                  <button className="text-red-500 font-bold text-sm tracking-wide flex items-center gap-1 group/btn">
                    LEARN MORE <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-black" data-testid="products-section-v3">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <div className="text-red-500 font-bold tracking-widest text-sm mb-3">EQUIPMENT & PRODUCTS</div>
              <h2 className="text-5xl md:text-6xl font-black tracking-tight">
                Products We <span className="text-red-600">Sell</span>
              </h2>
            </div>
            <div className="max-w-md">
              <p className="text-zinc-400 text-lg">
                Comprehensive lineup of industrial electrical equipment for oilfield, drilling, and manufacturing operations.
              </p>
              <p className="text-sm text-zinc-500 mt-2">
                Click any product to view full specifications and details.
              </p>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-bold tracking-wide transition ${
                  activeCategory === cat 
                    ? 'bg-red-600 text-white shadow-lg shadow-red-600/30' 
                    : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-red-600 hover:text-white'
                }`}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              >
                {cat.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div className="group bg-zinc-950 border border-zinc-900 hover:border-red-600 rounded-xl overflow-hidden transition-all duration-300 flex flex-col cursor-pointer hover:shadow-xl hover:shadow-red-600/20 hover:-translate-y-1"
                onClick={() => { setSelectedProduct(product); setCurrentImageIdx(0); }}
                key={product.id}
                data-testid={`product-${product.id}-v3`}
              >
                <div className="relative h-56 overflow-hidden bg-zinc-900">
                  <img src={product.img} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="text-[10px] text-red-500 font-bold tracking-[0.2em] mb-2">{product.category.toUpperCase()}</div>
                  <h3 className="text-xl font-black mb-3">{product.name}</h3>
                  <p className="text-zinc-400 text-sm mb-5 leading-relaxed flex-1 line-clamp-3">
                    {product.description}
                  </p>
                  <div className="text-red-500 font-bold text-sm tracking-wide flex items-center gap-2 mt-auto">
                    VIEW DETAILS
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schneider Electric Alliance Partner Badge */}
      <section className="py-12 bg-zinc-950 border-y border-zinc-900" data-testid="schneider-partner-section">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 max-w-4xl mx-auto">
            <img 
              src="/assets/schneider_alliance_badge.png" 
              alt="Schneider Electric Alliance Partner - Registered" 
              className="w-32 md:w-36 h-auto flex-shrink-0"
              data-testid="schneider-badge"
            />
            <div className="text-center md:text-left">
              <div className="text-[10px] text-red-500 font-bold tracking-[0.2em] mb-2">PROUD PARTNER</div>
              <h3 className="text-2xl md:text-3xl font-black mb-2">Schneider Electric Alliance Partner</h3>
              <p className="text-zinc-400 text-sm md:text-base max-w-xl">
                Registered Alliance Partner — providing authorized Schneider Electric solutions and expertise to our customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SALE Section - Used Motors & Camlock Connectors */}
      <section id="sale" className="py-24 bg-zinc-950" data-testid="sale-section-v3">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-red-500 font-bold tracking-widest text-sm mb-3">CURRENT SPECIALS</div>
            <h2 className="text-5xl md:text-6xl font-black tracking-tight mb-4">
              Sale <span className="text-red-600">Items</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Limited stock items available at special pricing. First come, first served.
            </p>
          </div>

          {/* Used Motor Stock */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-red-600/20 border border-red-600/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wrench size={22} className="text-red-500" />
              </div>
              <div>
                <div className="text-[10px] text-red-500 font-bold tracking-[0.2em] mb-1">USED MOTOR STOCK</div>
                <h3 className="text-3xl font-black">Available Motors</h3>
                <p className="text-sm text-zinc-400 mt-1">Click any motor to view full pricing and details.</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {usedMotors.map((motor, idx) => (
                <button
                  key={`${motor.brand}-${motor.model}`}
                  onClick={() => setSelectedMotor(motor)}
                  className="bg-black border border-zinc-900 hover:border-red-600 hover:bg-zinc-900 rounded-lg p-5 text-left transition-all duration-200 group hover:shadow-lg hover:shadow-red-600/20"
                  data-testid={`used-motor-${idx}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-xs text-red-500 font-bold tracking-widest">{motor.brand.toUpperCase()}</div>
                    <div className="text-xs text-zinc-500 bg-zinc-900 group-hover:bg-black px-2 py-0.5 rounded-full">
                      ({motor.stock}) in stock
                    </div>
                  </div>
                  <div className="text-sm font-mono text-white mb-2">{motor.model}</div>
                  <div className="text-xs text-zinc-500 group-hover:text-red-400 font-bold flex items-center gap-1 transition">
                    VIEW PRICE <ArrowRight size={12} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Camlock Connectors */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-red-600/20 border border-red-600/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package size={22} className="text-red-500" />
              </div>
              <div>
                <div className="text-[10px] text-red-500 font-bold tracking-[0.2em] mb-1">CAMLOCK CONNECTORS · $175 EACH</div>
                <h3 className="text-3xl font-black">Single-Pole Power Connectors</h3>
                <p className="text-sm text-zinc-400 mt-1 max-w-2xl">Heavy-duty 535 amp camlock connectors for temporary power on generators, load banks, pumps, and trailers. Available in all colors for phase identification.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {camlockImages.map((img, idx) => (
                <div key={img} className="bg-black border border-zinc-900 rounded-lg overflow-hidden hover:border-red-600 transition group">
                  <div className="h-48 overflow-hidden">
                    <img src={img} alt={`Camlock connectors ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-black border border-zinc-900 rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-black mb-3 text-red-500">Brands Available</h4>
                  <ul className="space-y-1.5 text-sm text-zinc-300">
                    <li>• Rig Power</li>
                    <li>• Crouse-Hinds</li>
                    <li>• Killark</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-black mb-3 text-red-500">Available Colors & Types</h4>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {['Green (Ground)', 'Orange', 'Purple/Pink', 'Brown', 'Red', 'Yellow', 'Gray', 'White', 'Black'].map(c => (
                      <span key={c} className="bg-zinc-900 border border-zinc-800 px-3 py-1 rounded-full text-zinc-300">{c}</span>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">Male (1M), Female (1F), and complete 535 amp assemblies available.</p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-4 border-t border-zinc-900">
                <div>
                  <div className="text-3xl font-black text-red-500">$175<span className="text-base text-zinc-400 font-normal"> each</span></div>
                  <p className="text-xs text-zinc-500 mt-1">Bulk pricing available — call for details</p>
                </div>
                <a href="tel:432-332-2000" className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-bold text-sm tracking-wide transition flex items-center gap-2">
                  <Phone size={16} /> CALL TO ORDER
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-black" data-testid="about-section-v3">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img src="/photos/custom_panel_build_2.jpeg" alt="Our Work" className="rounded-2xl shadow-2xl w-full" />
              <div className="absolute -bottom-6 -right-6 bg-red-600 text-white p-6 rounded-2xl shadow-2xl">
                <div className="text-5xl font-black">1987</div>
                <div className="text-sm tracking-widest">FOUNDED</div>
              </div>
            </div>
            <div>
              <div className="text-red-500 font-bold tracking-widest text-sm mb-3">ABOUT US</div>
              <h2 className="text-5xl font-black tracking-tight mb-6">
                Built in <span className="text-red-600">West Texas</span><br/>
                For West Texas
              </h2>
              <p className="text-zinc-400 text-lg mb-6 leading-relaxed">
                Founded in 1987, Impulse Electric specializes in delivering expert electrical power and drive solutions to manufacturers operating in remote and rugged environments. Over the decades, we've built a strong reputation for knowing what works — and what doesn't — in the challenging markets we serve.
              </p>
              <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                Our product range spans from small electric motors for water tanks to high-powered, thousand-plus horsepower motors that drive electric Drawworks on drilling platforms. We offer flexible installation options — at your facility or in our purpose-built shop. All of our electricians are certified and qualified to perform the work they do safely and efficiently.
              </p>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-950 border border-zinc-900 rounded-xl p-5 text-center">
                  <Shield className="text-red-500 mx-auto mb-2" size={32} />
                  <div className="text-sm font-bold">Certified</div>
                  <div className="text-xs text-zinc-400">Electricians</div>
                </div>
                <div className="bg-zinc-950 border border-zinc-900 rounded-xl p-5 text-center">
                  <Award className="text-red-500 mx-auto mb-2" size={32} />
                  <div className="text-sm font-bold">PLC</div>
                  <div className="text-xs text-zinc-400">Experienced</div>
                </div>
                <div className="bg-zinc-950 border border-zinc-900 rounded-xl p-5 text-center">
                  <Zap className="text-red-500 mx-auto mb-2" size={32} />
                  <div className="text-sm font-bold">24/7</div>
                  <div className="text-xs text-zinc-400">Emergency</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-zinc-950" data-testid="contact-section-v3">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="text-red-500 font-bold tracking-widest text-sm mb-3">GET IN TOUCH</div>
              <h2 className="text-5xl md:text-6xl font-black tracking-tight mb-4">
                Let's <span className="text-red-600">Talk</span>
              </h2>
              <p className="text-zinc-400 text-lg">Ready to start your project? Get in touch today.</p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-black border border-zinc-900 rounded-xl p-6 hover:border-red-600 transition">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 tracking-widest mb-1">CALL US</div>
                      <a href="tel:432-332-2000" className="text-2xl font-black hover:text-red-500 transition">432-332-2000</a>
                      <p className="text-sm text-zinc-400 mt-1">Fax: 432-332-2003</p>
                    </div>
                  </div>
                </div>

                <div className="bg-black border border-zinc-900 rounded-xl p-6 hover:border-red-600 transition">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 tracking-widest mb-1">EMAIL US</div>
                      <a href="mailto:sales@impulseelectric.com" className="text-lg font-black hover:text-red-500 transition break-all">sales@impulseelectric.com</a>
                    </div>
                  </div>
                </div>

                <div className="bg-black border border-zinc-900 rounded-xl p-6 hover:border-red-600 transition">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 tracking-widest mb-1">VISIT US</div>
                      <p className="font-bold">3030 W. Murphy St.</p>
                      <p className="text-zinc-400">Odessa, TX 79763</p>
                    </div>
                  </div>
                </div>

                <div className="bg-black border border-zinc-900 rounded-xl p-6 hover:border-red-600 transition">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 tracking-widest mb-1">HOURS</div>
                      <p className="font-bold">Mon - Fri: 8AM - 5PM</p>
                      <p className="text-red-500 font-bold">24/7 Emergency Service</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3 bg-black border border-zinc-900 rounded-xl p-8">
                <form onSubmit={handleSubmit} data-testid="contact-form-v3" className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-zinc-400 tracking-widest mb-2 font-bold">NAME *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full bg-zinc-950 border border-zinc-900 focus:border-red-600 rounded-lg px-4 py-3 text-white outline-none transition" data-testid="contact-name-input-v3" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 tracking-widest mb-2 font-bold">PHONE *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full bg-zinc-950 border border-zinc-900 focus:border-red-600 rounded-lg px-4 py-3 text-white outline-none transition" data-testid="contact-phone-input-v3" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-zinc-400 tracking-widest mb-2 font-bold">EMAIL *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full bg-zinc-950 border border-zinc-900 focus:border-red-600 rounded-lg px-4 py-3 text-white outline-none transition" data-testid="contact-email-input-v3" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 tracking-widest mb-2 font-bold">ORGANIZATION</label>
                      <input type="text" name="organization" value={formData.organization} onChange={handleInputChange} className="w-full bg-zinc-950 border border-zinc-900 focus:border-red-600 rounded-lg px-4 py-3 text-white outline-none transition" data-testid="contact-org-input-v3" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 tracking-widest mb-2 font-bold">INQUIRING ABOUT *</label>
                    <select name="inquiry_type" value={formData.inquiry_type} onChange={handleInputChange} required className="w-full bg-zinc-950 border border-zinc-900 focus:border-red-600 rounded-lg px-4 py-3 text-white outline-none transition" data-testid="contact-inquiry-select-v3">
                      <option>General Inquiry</option>
                      <option>Electric Pump Systems</option>
                      <option>Power Distribution & Control</option>
                      <option>Motors</option>
                      <option>Specialty Equipment</option>
                      <option>Consumables & Transfer</option>
                      <option>Enclosures</option>
                      <option>Used Motor Stock (SALE)</option>
                      <option>Camlock Connectors (SALE)</option>
                      <option>Generators (SALE)</option>
                      <option>Emergency Service</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 tracking-widest mb-2 font-bold">MESSAGE *</label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} required rows="4" className="w-full bg-zinc-950 border border-zinc-900 focus:border-red-600 rounded-lg px-4 py-3 text-white outline-none transition resize-none" data-testid="contact-message-textarea-v3"></textarea>
                  </div>
                  {formStatus.message && (
                    <div className={`p-4 rounded-lg ${formStatus.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`} data-testid="contact-form-status-v3">
                      {formStatus.message}
                    </div>
                  )}
                  <button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 px-6 py-4 rounded-lg font-black tracking-wide transition shadow-xl shadow-red-600/30 flex items-center justify-center gap-2" data-testid="contact-submit-v3">
                    {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                    {!isSubmitting && <ArrowRight size={20} />}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-900 py-12" data-testid="footer-v3">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/assets/impulse_logo.png" alt="Impulse Electric" className="w-10 h-10 object-contain rounded-lg" />
                <div>
                  <h3 className="font-black">IMPULSE ELECTRIC</h3>
                  <p className="text-[10px] text-red-500 tracking-widest font-bold">EST. 1987</p>
                </div>
              </div>
              <p className="text-sm text-zinc-500 italic">"From West Texas to the field — power that performs."</p>
            </div>
            <div>
              <h4 className="font-bold tracking-widest text-sm mb-4 text-red-500">QUICK LINKS</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-red-500 transition">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-red-500 transition">Services</button></li>
                <li><button onClick={() => scrollToSection('products')} className="hover:text-red-500 transition">Products</button></li>
                <li><button onClick={() => scrollToSection('sale')} className="hover:text-red-500 transition">Sale Items</button></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-red-500 transition">About</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold tracking-widest text-sm mb-4 text-red-500">PRODUCT CATEGORIES</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>Electric Pump Systems</li>
                <li>Power Distribution & Control</li>
                <li>AC / DC Motors</li>
                <li>Specialty Equipment</li>
                <li>Consumables & Transfer</li>
                <li>Enclosures</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold tracking-widest text-sm mb-4 text-red-500">CONTACT</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-start gap-2"><Phone size={14} className="mt-1 flex-shrink-0" /><a href="tel:432-332-2000" className="hover:text-red-500">432-332-2000</a></li>
                <li className="flex items-start gap-2"><Mail size={14} className="mt-1 flex-shrink-0" /><a href="mailto:sales@impulseelectric.com" className="hover:text-red-500 break-all">sales@impulseelectric.com</a></li>
                <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 flex-shrink-0" /><span>3030 W. Murphy St.<br/>Odessa, TX 79763</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-900 pt-8 text-center text-zinc-600 text-sm">
            <p>© {new Date().getFullYear()} Impulse Electric Ltd. All rights reserved. <span className="text-red-500">Powering West Texas Since 1987 ⚡</span></p>
          </div>
        </div>
      </footer>

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setSelectedService(null)} data-testid="service-modal">
          <div className="bg-zinc-950 border border-red-600 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const galleryImages = selectedService.images && selectedService.images.length > 0 ? selectedService.images : [selectedService.img];
              const hasMultiple = galleryImages.length > 1;
              return (
                <>
                  <div className="relative">
                    <img src={galleryImages[currentImageIdx]} alt={selectedService.title} className="w-full h-72 object-cover rounded-t-2xl" />
                    <button onClick={() => setSelectedService(null)} className="absolute top-4 right-4 w-10 h-10 bg-black/80 hover:bg-red-600 rounded-full flex items-center justify-center transition z-10" data-testid="close-service-modal">
                      <X size={20} />
                    </button>
                    {hasMultiple && (
                      <>
                        <button
                          onClick={() => setCurrentImageIdx((currentImageIdx - 1 + galleryImages.length) % galleryImages.length)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/80 hover:bg-red-600 rounded-full flex items-center justify-center transition"
                          data-testid="service-prev-image"
                        >
                          <ChevronRight size={20} className="rotate-180" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIdx((currentImageIdx + 1) % galleryImages.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/80 hover:bg-red-600 rounded-full flex items-center justify-center transition"
                          data-testid="service-next-image"
                        >
                          <ChevronRight size={20} />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs font-bold px-3 py-1 rounded-full">
                          {currentImageIdx + 1} / {galleryImages.length}
                        </div>
                      </>
                    )}
                  </div>
                  {hasMultiple && (
                    <div className="px-4 pt-4 pb-2 flex gap-2 overflow-x-auto">
                      {galleryImages.map((img, idx) => (
                        <button
                          key={img}
                          onClick={() => setCurrentImageIdx(idx)}
                          className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition ${currentImageIdx === idx ? 'border-red-600' : 'border-zinc-800 hover:border-zinc-600'}`}
                        >
                          <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="p-8 pt-4">
                    <div className="text-[10px] text-red-500 font-bold tracking-[0.2em] mb-2">OUR SERVICES</div>
                    <h3 className="text-3xl md:text-4xl font-black mb-4">{selectedService.title}</h3>
                    <p className="text-zinc-300 text-base mb-6 leading-relaxed">{selectedService.desc}</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a href="tel:432-332-2000" className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold text-center transition flex items-center justify-center gap-2">
                        <Phone size={18} /> CALL FOR SERVICE
                      </a>
                      <button onClick={() => { setSelectedService(null); scrollToSection('contact'); }} className="flex-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-6 py-3 rounded-lg font-bold transition">
                        REQUEST INFO
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setSelectedProduct(null)} data-testid="product-modal">
          <div className="bg-zinc-950 border border-red-600 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const galleryImages = selectedProduct.images && selectedProduct.images.length > 0 ? selectedProduct.images : [selectedProduct.img];
              const hasMultiple = galleryImages.length > 1;
              return (
                <>
                  <div className="relative">
                    <img src={galleryImages[currentImageIdx]} alt={selectedProduct.name} className="w-full h-72 object-cover rounded-t-2xl" />
                    <button onClick={() => setSelectedProduct(null)} className="absolute top-4 right-4 w-10 h-10 bg-black/80 hover:bg-red-600 rounded-full flex items-center justify-center transition z-10" data-testid="close-product-modal">
                      <X size={20} />
                    </button>
                    {hasMultiple && (
                      <>
                        <button
                          onClick={() => setCurrentImageIdx((currentImageIdx - 1 + galleryImages.length) % galleryImages.length)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/80 hover:bg-red-600 rounded-full flex items-center justify-center transition"
                          data-testid="prev-image"
                        >
                          <ChevronRight size={20} className="rotate-180" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIdx((currentImageIdx + 1) % galleryImages.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/80 hover:bg-red-600 rounded-full flex items-center justify-center transition"
                          data-testid="next-image"
                        >
                          <ChevronRight size={20} />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs font-bold px-3 py-1 rounded-full">
                          {currentImageIdx + 1} / {galleryImages.length}
                        </div>
                      </>
                    )}
                  </div>
                  {hasMultiple && (
                    <div className="px-4 pt-4 pb-2 flex gap-2 overflow-x-auto">
                      {galleryImages.map((img, idx) => (
                        <button
                          key={img}
                          onClick={() => setCurrentImageIdx(idx)}
                          className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition ${currentImageIdx === idx ? 'border-red-600' : 'border-zinc-800 hover:border-zinc-600'}`}
                        >
                          <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="p-8 pt-4">
                    <div className="text-[10px] text-red-500 font-bold tracking-[0.2em] mb-2">{selectedProduct.category.toUpperCase()}</div>
                    <h3 className="text-3xl md:text-4xl font-black mb-4">{selectedProduct.name}</h3>
                    <p className="text-zinc-300 text-base mb-6 leading-relaxed">{selectedProduct.description}</p>
                    {selectedProduct.details && selectedProduct.details.length > 0 && (
                      <div className="bg-black border border-zinc-900 rounded-lg p-5 mb-6">
                        <h4 className="text-red-500 font-bold tracking-widest text-xs mb-3">SPECIFICATIONS & FEATURES</h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {selectedProduct.details.map((d) => (
                            <li key={d} className="flex items-start gap-2 text-sm text-zinc-300">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedProduct.priceTables && selectedProduct.priceTables.length > 0 && (
                      <div className="mb-6 space-y-4" data-testid="product-price-tables">
                        {selectedProduct.priceTables.map((table) => (
                          <div key={table.title} className="bg-black border border-zinc-900 rounded-lg overflow-hidden">
                            <div className="bg-red-600/10 border-b border-red-600/30 px-5 py-3 flex items-center justify-between">
                              <h4 className="text-red-500 font-bold tracking-widest text-xs">{table.title.toUpperCase()}</h4>
                              <span className="text-[10px] text-zinc-500 font-bold tracking-widest">RETAIL</span>
                            </div>
                            <ul className="divide-y divide-zinc-900">
                              {table.rows.map((row) => (
                                <li key={row[0]} className="flex justify-between items-center gap-4 px-5 py-3">
                                  <span className="text-sm text-zinc-200">{row[0]}</span>
                                  <span className="text-sm font-black text-white whitespace-nowrap">{row[1]}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        <p className="text-[11px] text-zinc-500 tracking-wide">Prices subject to change. Accessories may be sold separately — call for a quote.</p>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a href="tel:432-332-2000" className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold text-center transition flex items-center justify-center gap-2">
                        <Phone size={18} /> CALL FOR QUOTE
                      </a>
                      <button onClick={() => { setSelectedProduct(null); scrollToSection('contact'); }} className="flex-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-6 py-3 rounded-lg font-bold transition">
                        REQUEST INFO
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Motor Detail Modal */}
      {selectedMotor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setSelectedMotor(null)} data-testid="motor-modal">
          <div className="bg-zinc-950 border border-red-600 rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 relative">
              <button onClick={() => setSelectedMotor(null)} className="absolute top-4 right-4 w-10 h-10 bg-zinc-900 hover:bg-red-600 rounded-full flex items-center justify-center transition" data-testid="close-motor-modal">
                <X size={20} />
              </button>
              <div className="text-xs text-red-500 font-bold tracking-widest mb-2">USED MOTOR · {selectedMotor.notes.toUpperCase()}</div>
              <div className="text-3xl font-black mb-1">{selectedMotor.brand}</div>
              <div className="text-zinc-400 font-mono mb-6">{selectedMotor.model}</div>
              
              <div className="bg-black border border-zinc-900 rounded-lg p-5 mb-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-zinc-500 tracking-widest font-bold">QUANTITY IN STOCK</span>
                  <span className="text-2xl font-black text-red-500">({selectedMotor.stock})</span>
                </div>
                <div className="border-t border-zinc-900 pt-3">
                  <div className="text-xs text-zinc-500 tracking-widest font-bold mb-1">PRICE</div>
                  <div className="text-3xl font-black text-white">{selectedMotor.price}</div>
                </div>
              </div>

              <p className="text-sm text-zinc-400 mb-6">
                Photos available upon request. Call or email to confirm current availability and arrange viewing.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="tel:432-332-2000" className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold text-center transition flex items-center justify-center gap-2">
                  <Phone size={18} /> 432-332-2000
                </a>
                <a href="mailto:sales@impulseelectric.com" className="flex-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-6 py-3 rounded-lg font-bold transition text-center flex items-center justify-center gap-2">
                  <Mail size={18} /> EMAIL
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppV3;
